import {numGroup} from '@entities/relations/utils';

import {AuditStatisticsDto, MonthStatValueDto} from './types';

export type NormalizedMonthStats = {
    year: number;
    month: number;
    [code: string]: number;
};

export const VIOLATION_STATUS_CODE = {
    OPENED: 'OPENED',
    CLOSED: 'CLOSED',
} as const;

export const NOTIFICATION_STATUS_CODE = {
    SENT: 'SENT',
    SEND_ERROR: 'SEND_ERROR',
} as const;

const VIOLATION_STATUS_CODES = Object.values(VIOLATION_STATUS_CODE);
const NOTIFICATION_STATUS_CODES = Object.values(NOTIFICATION_STATUS_CODE);

export class MonthStatValue {
    year: number;
    month: number;
    value: number;

    constructor(stats?: MonthStatValueDto | null) {
        this.year = stats?.year ?? 0;
        this.month = stats?.month ?? 0;
        this.value = stats?.value ?? 0;
    }
}

const mapMonthStats = (items?: MonthStatValueDto[]) =>
    (items ?? []).map((item) => new MonthStatValue(item));

const getInitialStats = (violationTypeCodes: string[]): AuditStatisticsDto => ({
    foundViolationsCount: 0,
    foundViolationsSum: 0,
    closedViolationsCount: 0,
    closedViolationsSum: 0,
    legalPersonsWithOpenedViolations: 0,
    legalPersonsWithOpenedViolationsSum: 0,
    legalPersonsWithClosedViolations: 0,
    legalPersonsWithClosedViolationsSum: 0,
    notificationsSent: 0,
    violationsByType: violationTypeCodes.reduce<Record<string, number>>((acc, code) => {
        acc[code] = 0;
        return acc;
    }, {}),
    violationsByTypeTrend: violationTypeCodes.reduce<Record<string, MonthStatValueDto[]>>(
        (acc, code) => {
            acc[code] = [];
            return acc;
        },
        {},
    ),
    notificationsByStatus: NOTIFICATION_STATUS_CODES.reduce<Record<string, MonthStatValueDto[]>>(
        (acc, code) => {
            acc[code] = [];
            return acc;
        },
        {},
    ),
    taxPayersByViolationStatus: VIOLATION_STATUS_CODES.reduce<Record<string, MonthStatValueDto[]>>(
        (acc, code) => {
            acc[code] = [];
            return acc;
        },
        {},
    ),
});

export class AuditStatisticsEntity {
    foundViolationsCount: number;
    foundViolationsSum: number;
    closedViolationsCount: number;
    closedViolationsSum: number;
    legalPersonsWithOpenedViolations: number;
    legalPersonsWithOpenedViolationsSum: number;
    legalPersonsWithClosedViolations: number;
    legalPersonsWithClosedViolationsSum: number;
    notificationsSent: number;
    violationsByType: Record<string, number>;
    violationsByTypeTrend: Record<string, MonthStatValue[]>;
    notificationsByStatus: Record<string, MonthStatValue[]>;
    taxPayersByViolationStatus: Record<string, MonthStatValue[]>;

    constructor(stats: AuditStatisticsDto | null, violationTypeCodes: string[]) {
        const data = stats ?? getInitialStats(violationTypeCodes);

        this.foundViolationsCount = data.foundViolationsCount;
        this.foundViolationsSum = data.foundViolationsSum;
        this.closedViolationsCount = data.closedViolationsCount;
        this.closedViolationsSum = data.closedViolationsSum;
        this.legalPersonsWithOpenedViolations = data.legalPersonsWithOpenedViolations;
        this.legalPersonsWithOpenedViolationsSum = data.legalPersonsWithOpenedViolationsSum;
        this.legalPersonsWithClosedViolations = data.legalPersonsWithClosedViolations;
        this.legalPersonsWithClosedViolationsSum = data.legalPersonsWithClosedViolationsSum;
        this.notificationsSent = data.notificationsSent;
        this.violationsByType = data.violationsByType ?? {};
        this.violationsByTypeTrend = Object.fromEntries(
            Object.entries(data.violationsByTypeTrend ?? {}).map(([key, value]) => [
                key,
                mapMonthStats(value),
            ]),
        );
        this.notificationsByStatus = Object.fromEntries(
            Object.entries(data.notificationsByStatus ?? {}).map(([key, value]) => [
                key,
                mapMonthStats(value),
            ]),
        );
        this.taxPayersByViolationStatus = Object.fromEntries(
            Object.entries(data.taxPayersByViolationStatus ?? {}).map(([key, value]) => [
                key,
                mapMonthStats(value),
            ]),
        );
    }

    get foundViolationsCountNumGroup() {
        return numGroup(this.foundViolationsCount, {fractionDigits: 0});
    }

    get foundViolationsSumNumGroup() {
        return numGroup(this.foundViolationsSum, {fractionDigits: 0});
    }

    get closedViolationsCountNumGroup() {
        return numGroup(this.closedViolationsCount, {fractionDigits: 0});
    }

    get closedViolationsSumNumGroup() {
        return numGroup(this.closedViolationsSum, {fractionDigits: 0});
    }

    get legalPersonsWithOpenedViolationsNumGroup() {
        return numGroup(this.legalPersonsWithOpenedViolations, {fractionDigits: 0});
    }

    get legalPersonsWithOpenedViolationsSumNumGroup() {
        return numGroup(this.legalPersonsWithOpenedViolationsSum, {fractionDigits: 0});
    }

    get legalPersonsWithClosedViolationsNumGroup() {
        return numGroup(this.legalPersonsWithClosedViolations, {fractionDigits: 0});
    }

    get legalPersonsWithClosedViolationsSumNumGroup() {
        return numGroup(this.legalPersonsWithClosedViolationsSum, {fractionDigits: 0});
    }

    get notificationsSentNumGroup() {
        return numGroup(this.notificationsSent, {fractionDigits: 0});
    }

    get violationsByTypeTotal() {
        return Object.values(this.violationsByType).reduce((acc, value) => acc + (value ?? 0), 0);
    }

    getViolationsByTypeNumValue(key: string) {
        return this.violationsByType[key] ?? 0;
    }

    getViolationsByTypePercentValue(key: string) {
        const value = this.violationsByType[key];
        if (value == null || this.violationsByTypeTotal === 0) return '0';
        return ((value / this.violationsByTypeTotal) * 100).toFixed(2);
    }

    normalizeTaxPayersByViolationStatus(): NormalizedMonthStats[] {
        return this.gatherData(
            this.gatherTaxPeriods(VIOLATION_STATUS_CODES, this.taxPayersByViolationStatus),
            VIOLATION_STATUS_CODES,
            this.taxPayersByViolationStatus,
        );
    }

    normalizeViolationsByTypeTrend(): NormalizedMonthStats[] {
        const keys = Object.keys(this.violationsByTypeTrend);
        return this.gatherData(
            this.gatherTaxPeriods(keys, this.violationsByTypeTrend),
            keys,
            this.violationsByTypeTrend,
        );
    }

    normalizeNotificationsByStatus(): NormalizedMonthStats[] {
        return this.gatherData(
            this.gatherTaxPeriods(NOTIFICATION_STATUS_CODES, this.notificationsByStatus),
            NOTIFICATION_STATUS_CODES,
            this.notificationsByStatus,
        );
    }

    private gatherTaxPeriods(keys: string[], statsObject: Record<string, MonthStatValue[]>) {
        const periods = keys
            .flatMap((key) => statsObject[key] ?? [])
            .filter((item) => item.year && item.month)
            .map((item) => ({
                year: item.year,
                month: item.month,
                value: item.year * 100 + item.month,
            }));

        return periods
            .filter(
                (period, index, list) =>
                    list.findIndex((item) => item.value === period.value) === index,
            )
            .sort((a, b) => a.value - b.value);
    }

    private gatherData(
        taxPeriods: Array<{year: number; month: number}>,
        keys: string[],
        statsObject: Record<string, MonthStatValue[]>,
    ): NormalizedMonthStats[] {
        return taxPeriods.map((period) => ({
            year: period.year,
            month: period.month,
            ...keys.reduce<Record<string, number>>((acc, key) => {
                const item = statsObject[key]?.find(
                    (value) => value.year === period.year && value.month === period.month,
                );
                acc[key] = item?.value ?? 0;
                return acc;
            }, {}),
        }));
    }
}
